import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useReadyAuth } from "@/auth";
import { useActionHistory, useSignalActions, useSignalDetail } from "../../hooks";
import { ModalHeader } from "./ModalHeader";
import { ModalMain } from "./ModalMain";
import { ModalRail } from "./ModalRail";

/**
 * M1 · SignalDetailModal (Reskin Session B). Opens over the feed at /signals/:id
 * (nested route, so the feed layout never unmounts and its scroll is preserved
 * on close). Rebuilt to the sealed detail mock: a floating cream modal on a
 * blurred teal scrim, a floating-glass header, and a two-column body. The header
 * is part of the scrolling modal surface (not a stuck overlay), per the mock.
 *
 * Focus is trapped inside; ESC and scrim click return to the feed keeping the
 * active tab query. Instant paint is preserved: the modal renders from the
 * card-derived feed-cache row on the click tick and swaps detail-only fields in
 * as get_signal_detail lands (see `hydrating`).
 */
export function SignalDetailModal(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useReadyAuth();
  const viewerId = user.id;
  const isAdmin = profile.role === "admin" || profile.role === "super_admin";
  const detail = useSignalDetail(id ?? null, viewerId);
  const actions = useSignalActions(id ?? "");
  const history = useActionHistory(id ?? null);

  const modalRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    navigate(`/signals${location.search}`);
  }, [navigate, location.search]);

  // ESC to close + basic focus trap; lock background scroll while open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    modalRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [close]);

  const signal = detail.data ?? null;
  const hydrating = detail.isPlaceholderData;

  return (
    <div className="sx-modal-overlay" onClick={close} role="presentation">
      <div
        className="sx-modal"
        role="dialog"
        aria-modal="true"
        aria-label={signal ? `Signal detail: ${signal.company}` : "Signal detail"}
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="sx-modal-close" onClick={close} aria-label="Close signal">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* `signal` is the row the viewer just clicked, seeded from the feed cache,
            so the modal paints on the click tick. Check for a signal FIRST: a
            detail fetch still in flight (or that failed) must not blank out
            content we can already show. */}
        {signal ? (
          <>
            <ModalHeader signal={signal} viewerId={viewerId} isAdmin={isAdmin} actions={actions} />
            {detail.isError ? (
              <p className="sx-inline-error" role="status">
                Couldn't load the full detail.{" "}
                <button type="button" className="sx-btn sx-btn-p3" onClick={() => detail.refetch()}>
                  Retry
                </button>
              </p>
            ) : null}
            <div className="sx-grid">
              <div className="sx-col">
                <ModalMain
                  signal={signal}
                  viewerId={viewerId}
                  actions={actions}
                  hydrating={hydrating}
                />
              </div>
              <div className="sx-col">
                <ModalRail
                  signal={signal}
                  viewerId={viewerId}
                  isAdmin={isAdmin}
                  actions={actions}
                  history={history.data ?? []}
                  historyLoading={history.isPending}
                />
              </div>
            </div>
          </>
        ) : detail.isPending ? (
          <div className="sx-modal-state">
            <span className="sx-skel" style={{ width: 200, height: 14 }} />
            <span className="sx-skel" style={{ width: 320, height: 12 }} />
          </div>
        ) : detail.isError ? (
          <div className="sx-modal-state">
            <p className="sx-modal-state-title">This signal didn't load.</p>
            <button type="button" className="sx-btn sx-btn-p2" onClick={() => detail.refetch()}>
              Retry
            </button>
          </div>
        ) : (
          <div className="sx-modal-state">
            <p className="sx-modal-state-title">Signal not found.</p>
            <button type="button" className="sx-btn sx-btn-p2" onClick={close}>
              Back to feed
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
